import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';
import { commaEvery3rdChar } from '../../../../../../utils';
import { formatFyEnd } from '../../../../../../utils/fiscalYear';
import { MILLIONS_MULTIPLIER } from '../../../subjectCompanyMetrics';

export default function PubCoGrowth({ reportData, totalPageCount, exhibitNumber }) {
  const columnOrder = [
    'Company Name',
    'Last FYE Date',
    'Market Capitalization | Valuation Date',
    'Enterprise Value | Valuation Date',
    'Revenue | LTM',
    'Revenue Growth | LFY',
    'Revenue Growth | LTM',
    'Revenue Growth | CFY',
    'EBITDA Growth | LFY',
    'EBITDA Growth | LTM',
    'EBITDA Growth | CFY',
  ];

  const gridData = reportData.PublicCompanyMethod.grids;
  const table1Headers = Object.keys(gridData.growth).filter((header) => columnOrder.includes(header));
  const tableData = gridData.growth['Company Name'];

  const lfyEnd = formatFyEnd(reportData.companyInfo.valuationDate, 'LFY');
  const cfyEnd = formatFyEnd(reportData.companyInfo.valuationDate, 'CFY');
  const nfyEnd = formatFyEnd(reportData.companyInfo.valuationDate, 'NFY');
  const nfyPlus1End = formatFyEnd(reportData.companyInfo.valuationDate, 'NFY+1');
  const nfyPlus2End = formatFyEnd(reportData.companyInfo.valuationDate, 'NFY+2');
  const valuationDate = moment(reportData.companyInfo.valuationDate).format('MM/DD/YYYY');
  const valuationDatePlusOne = moment(reportData.companyInfo.valuationDate).add(1, 'year').format('MM/DD/YYYY');
  const valuationDatePlusTwo = moment(reportData.companyInfo.valuationDate).add(2, 'year').format('MM/DD/YYYY');
  const valuationDatePlusThree = moment(reportData.companyInfo.valuationDate).add(3, 'year').format('MM/DD/YYYY');

  const kpiMapper = {
    'Company Name': 'Company name',
    'Last FYE Date': 'FYE',
    'Market Capitalization | Valuation Date': 'Market\ncapitalization',
    'Enterprise Value | Valuation Date': 'Enterprise\nvalue',
    'Revenue | LTM': 'LTM\nrevenue',
    'Revenue Growth | LFY': 'LFY',
    'Revenue Growth | LTM': `LTM\n${valuationDate}`,
    'Revenue Growth | CFY': 'CFY',
    'Revenue Growth | NFY+1': `NFY+1\n${nfyPlus1End}`,
    'Revenue Growth | NFY+2': `NFY+2\n${nfyPlus2End}`,
    'EBITDA Growth | LFY': 'LFY',
    'EBITDA Growth | LTM': `LTM\n${valuationDate}`,
    'EBITDA Growth | CFY': 'CFY',
    'EBITDA Growth | NTM': `NTM\n${valuationDatePlusOne}`,
    'EBITDA Growth | NTM+1': `NTM+1\n${valuationDatePlusTwo}`,
    'EBITDA Growth | NTM+2': `NTM+2\n${valuationDatePlusThree}`,
    'EBITDA Growth | NFY': `NFY\n${nfyEnd}`,
    'EBITDA Growth | NFY+1': `NFY+1\n${nfyPlus1End}`,
    'EBITDA Growth | NFY+2': `NFY+2\n${nfyPlus2End}`,
  };


  function sortColumnNames(names, order) {
    const sortedNames = names.filter((name) => order.includes(name));
    sortedNames.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return sortedNames;
  }

  const hasFye = (gridData.growth?.['Last FYE Date'] || []).some((value) => value);
  const sortedHeaders = sortColumnNames(table1Headers, columnOrder)
    .filter((header) => header !== 'Last FYE Date' || hasFye);

  const emptyValues = [null, undefined, '', 'N/A'];
  const millionValueColumns = ['Market Capitalization | Valuation Date', 'Enterprise Value | Valuation Date', 'Revenue | LTM'];
  const fullValuePlacementColumns = [
    'Market Capitalization | Valuation Date',
    'Enterprise Value | Valuation Date',
    'Revenue | LTM',
  ];

  function hasValue(value) {
    return !emptyValues.includes(value);
  }

  function parseNumber(value) {
    if (!hasValue(value)) return null;
    const parsed = parseFloat(String(value).replace(/[$,%]/g, '').replaceAll(',', ''));
    return Number.isNaN(parsed) ? null : parsed;
  }

  function formatCurrencyInMillions(value) {
    const numericValue = parseNumber(value);
    if (numericValue === null) return 'N/A';
    return `$${commaEvery3rdChar(numericValue.toFixed(2))}`;
  }

  function formatRank(rank) {
    if (!hasValue(rank)) return 'N/A';
    const text = String(rank).trim();
    if (/^\d+\s*\/\s*\d+$/.test(text)) {
      return text.replace(/\s*\/\s*/, ' / ');
    }
    const numericRank = Number(text);
    if (Number.isInteger(numericRank) && numericRank > 0 && numericRank <= 1000) {
      return String(numericRank);
    }
    return 'N/A';
  }

  function formatPercent(value) {
    const numericValue = parseNumber(value);
    if (numericValue === null) return 'N/A';
    const percentValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
    return `${commaEvery3rdChar(percentValue.toFixed(2))}%`;
  }

  // Growth values are always stored as raw ratios and can legitimately exceed +/-100%
  // (e.g. 1.7596 for +175.96%), so they must always be scaled by 100, unlike margins.
  function formatGrowthPercent(value) {
    if (value === 'NM') return 'NM';
    const numericValue = parseNumber(value);
    if (numericValue === null) return 'N/A';
    if (numericValue < 0 && Math.abs(numericValue) > 1) return 'NM';
    return `${commaEvery3rdChar((numericValue * 100).toFixed(2))}%`;
  }

  function getSubjectPlacementValue(dataName, rowIndex) {
    if (rowIndex !== 0) return null;

    const metrics = reportData.subjectCompanyMetrics || {};

    if (dataName === 'Market Capitalization | Valuation Date') {
      return metrics.marketCapitalization ?? null;
    }

    if (dataName === 'Enterprise Value | Valuation Date') {
      return metrics.enterpriseValue ?? null;
    }

    return null;
  }

  function formatPlacementCell(dataName, rowIndex) {
    if (dataName === 'Last FYE Date') return 'N/A';
    if (rowIndex === 1) {
      return formatRank(gridData.placement?.[dataName]?.[1]);
    }
    if (rowIndex === 2) {
      const percentile = gridData.placement?.[dataName]?.[2];
      return hasValue(percentile) ? String(percentile) : 'N/A';
    }
    const subjectValue = getSubjectPlacementValue(dataName, rowIndex);
    const rawValue = gridData.placement?.[dataName]?.[rowIndex];
    const hasSubjectValue = subjectValue !== null && subjectValue !== undefined;
    const cellValue = hasSubjectValue ? subjectValue : (hasValue(rawValue) ? rawValue : null);

    if (cellValue === null || cellValue === undefined || cellValue === '') return 'N/A';
    if (hasSubjectValue && fullValuePlacementColumns.includes(dataName)) {
      return formatCurrencyInMillions(Number(cellValue) / MILLIONS_MULTIPLIER);
    }
    if (millionValueColumns.includes(dataName)) return formatCurrencyInMillions(cellValue);
    if (dataName.includes('Growth')) return formatGrowthPercent(cellValue);
    if (dataName.includes('Margin')) return formatPercent(cellValue);
    return cellValue;
  }

  function returnCellData(cellData, dataName, rowIndex, columnIndex, table) {
    let grid;
    if (table === 'placement') {
      return formatPlacementCell(dataName, rowIndex);
    }
    if (table === 'firstTable')grid = gridData.growth;
    if (table === 'summaryFirstTable') grid = gridData.growthSummary;
    if (table === 'thirdTable') grid = gridData.selection;
    if (cellData === 'selectedMultiple' || cellData === 'relativeSelection') grid = gridData.selection;
    const cellValue = grid?.[dataName]?.[rowIndex];
    if (dataName === 'Company Name') {
      const multiplesName = gridData.multiples?.['Company Name']?.[rowIndex];
      const raw = (typeof cellValue === 'string' && (cellValue.includes('…') || cellValue.endsWith('...')))
        ? (multiplesName || cellValue)
        : (cellValue || multiplesName);
      return raw ? String(raw) : 'N/A';
    }
    if (dataName === 'Last FYE Date') {
      if (table === 'summaryFirstTable') return '';
      return cellValue ? String(cellValue) : '-';
    }
    if (cellValue === null || cellValue === undefined || cellValue === '') return 'N/A';
    if (cellValue === 'NM') return 'NM';
    if (millionValueColumns.includes(dataName)) {
      return `$${commaEvery3rdChar(parseFloat(cellValue).toFixed(2))}`;
    }
    if (dataName.includes('Growth')) {
      const numericValue = parseFloat(cellValue);
      if (!Number.isFinite(numericValue)) return 'N/A';
      if (numericValue < 0 && Math.abs(numericValue) > 1) return 'NM';
      return `${commaEvery3rdChar((numericValue * 100).toFixed(2))}%`;
    }
    return cellValue || 'N/A';
  }

  function columnWidth(index) {
    if (index === 0) return hasFye ? '188px' : '220px';
    if (hasFye && index === 1) return '78px';
    return hasFye ? '94px' : '104px';
  }

  const numberOfPages = (Math.ceil((tableData.length + 10) / 37)) || 1;

  function customMap(array, callback, startIndex = 0) {
    return array.map((element, index) => callback(element, index + startIndex));
  }

  return (
    <>
      {[...Array(numberOfPages)].map((_, i) => i).map((pageNumber, pageIndex) => (
        <Page
          key={pageNumber}
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
          <TablePageHeader
            companyName={reportData.companyInfo.companyName}
            pageName={`Public Company Growth & Margins   |   Exhibit ${exhibitNumber}`}
          />
          <View
            style={{
              marginTop: '64px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '24px',
                backgroundColor: '#0C2617',
              }}
            >
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#8AB9A4',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  marginRight: '8px',
                  width: '652px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                (USD $Millions)
              </Text>
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  marginRight: '8px',
                  width: '250px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                Sales growth
              </Text>
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  width: '240px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                EBITDA growth
              </Text>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '40px',
                backgroundColor: '#307157',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {sortedHeaders.map((dataName, index) => (
                <Text
                  key={dataName}
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#E1E2EC',
                    width: columnWidth(index),
                    flexShrink: 0,
                    marginTop: 'auto',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    letterSpacing: '0.4px',
                    lineHeight: '0.55mm',
                    textAlign: index !== 0 && 'right',
                    ...index !== 0 && { left: 'auto' },
                  }}
                >
                  {kpiMapper[dataName]}
                </Text>
              ))}
            </View>
            {customMap(tableData.slice(pageIndex * 37, (pageIndex + 1) * 37), (cellData, rowIndex) => (
              <View
                key={cellData}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {sortedHeaders.map((dataName, columnIndex) => (
                  <Text
                    key={dataName}
                    wrap={columnIndex !== 0}
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: columnWidth(columnIndex),
                      flexShrink: 0,
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      letterSpacing: '0.4px',
                      ...columnIndex !== 0 && { textAlign: 'right' },
                    }}
                  >
                    {returnCellData(cellData, dataName, rowIndex, columnIndex, 'firstTable')|| 'N/A'}
                  </Text>
                ))}
              </View>
            ), pageIndex * 37)}
            {[...Array(numberOfPages)].length - 1 === pageIndex && (
              <>
                {gridData.placement.headers.map((cellData, rowIndex) => (

                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: '16px',
                      backgroundColor: '#9ECEBB',
                      borderTop: '1px',
                      borderColor: '#EAE9EC',
                    }}
                  >
                    {sortedHeaders.map((dataName, cellIndex) => (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: 'RobotoMed',
                          fontSize: '10px',
                          color: '#49454F',
                          width: columnWidth(cellIndex),
                          flexShrink: 0,
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          letterSpacing: '0.4px',
                          textAlign: cellIndex !== 0 && 'right',
                        }}
                      >
                        {cellIndex === 0 && rowIndex === 0 ? reportData.companyInfo.companyName :
                          cellIndex === 0 && rowIndex === 1 ? 'Rank' :
                            cellIndex === 0 && rowIndex === 2 ? 'Percentile Rank' :
                              cellIndex > 0 ? returnCellData('', dataName, rowIndex, 0, 'placement') :
                                'N/A'}
                      </Text>
                    ))}
                  </View>
                ))}
              </>
            )}
          </View>
          {tableData.slice(pageIndex * 37, (pageIndex + 1) * 37).length < 18 && (
            <View
              style={{
                marginTop: '16px',
                marginLeft: '32px',
                display: 'flex',
                flexDirection: 'column',
                width: '1160px',
                border: '1px',
                borderColor: '#AFB1B3',
              }}
            >
              {[
                ['Maximum', 'max'], ['95th percentile', '95%'],
                ['90th percentile', '90%'], ['75th percentile', '75%'],
                ['Median', '50%'], ['25th percentile', '25%'],
                ['10th percentile', '10%'], ['5th percentile', '5%'],
                ['Minimum', 'min'], ['Average', 'mean'],
              ].map((rowName, rowIndex) => (
                <View
                  key={rowName}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: '16px',
                    backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                  }}
                >
                  {sortedHeaders.map((dataName, cellIndex) => (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                        fontSize: '10px',
                        color: '#49454F',
                        width: columnWidth(cellIndex),
                        flexShrink: 0,
                        paddingLeft: '16px',
                        paddingRight: '4px',
                        letterSpacing: '0.4px',
                        textAlign: cellIndex !== 0 && 'right',
                      }}
                    >
                      {cellIndex === 0 ? rowName[0] :
                        returnCellData(rowName, dataName, rowIndex, cellIndex, 'summaryFirstTable') || 'N/A'}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          )}
          <Text
            style={{
              marginTop: '10px',
              marginLeft: '32px',
              width: '1160px',
              fontFamily: 'Roboto',
              fontSize: '8px',
              color: '#49454F',
              letterSpacing: '0.2px',
            }}
          >
            LFY and CFY follow each comparable&apos;s own fiscal year-end as of the valuation date, not a single 6/30 calendar. Amounts are translated to USD at historical rates. Growth is NM when the prior-period base is zero or negative.
          </Text>
          <BottomPagination
            pageNumber={totalPageCount + pageIndex}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      ))}
      {tableData.slice((numberOfPages - 1) * 37, numberOfPages * 37).length > 18 && (
        <Page
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
          <TablePageHeader companyName={reportData.companyInfo.companyName} pageName={`Public Company Growth & Margins   |   Exhibit ${exhibitNumber}`} />
          <View
            style={{
              marginTop: '16px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            {[
              ['Maximum', 'max'], ['95th percentile', '95%'],
              ['90th percentile', '90%'], ['75th percentile', '75%'],
              ['Median', '50%'], ['25th percentile', '25%'],
              ['10th percentile', '10%'], ['5th percentile', '5%'],
              ['Minimum', 'min'], ['Average', 'mean'],
            ].map((rowName, rowIndex) => (
              <View
                key={rowName[0]}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {sortedHeaders.map((dataName, cellIndex) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: columnWidth(cellIndex),
                      flexShrink: 0,
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      letterSpacing: '0.4px',
                      ...cellIndex !== 0 && { textAlign: 'right' },
                    }}
                  >
                    {cellIndex === 0 ? rowName[0] :
                      returnCellData(rowName, dataName, rowIndex, cellIndex, 'summaryFirstTable') || 'N/A'}
                  </Text>
                ))}
              </View>
            ))}
          </View>
          <BottomPagination
            pageNumber={totalPageCount + numberOfPages}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      )}
    </>
  );
}

PubCoGrowth.propTypes = {
  reportData: PropTypes.object.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
};
